import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Checkbox, FormControlLabel, makeStyles } from '@material-ui/core'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import SydModal from '../../commonDesign/SydModal'
import SydModalActions from '../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../commonDesign/Button'
import Icon from '../../atoms/Icon'
import { GROUPING_OPTIONS } from './lib/constants'

const DRAG_TYPE = 'GROUPING_ITEM'

const useStyles = makeStyles(() => ({
  body: {
    display: 'flex',
    gap: 16,
    minHeight: 300
  },
  column: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  columnHeader: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: 'var(--color-neutral-600, #666)',
    marginBottom: 8
  },
  optionList: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  optionLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: 13
    }
  },
  selectedList: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    border: '1px solid var(--opal-border-default, #e0e0e0)',
    borderRadius: 'var(--border-radius-sm, 4px)',
    padding: 4,
    minHeight: 100
  },
  dragItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 8px',
    background: 'var(--color-neutral-50, #fafafa)',
    borderRadius: 'var(--border-radius-sm, 4px)',
    fontSize: 13,
    cursor: 'grab',
    userSelect: 'none',
    '&:hover': {
      background: 'var(--color-neutral-100, #f0f0f0)'
    }
  },
  dragItemDragging: {
    opacity: 0.4
  },
  dragHandle: {
    color: 'var(--color-neutral-400, #aaa)',
    flexShrink: 0
  },
  dragLabel: {
    flex: 1
  },
  removeBtn: {
    cursor: 'pointer',
    color: 'var(--color-neutral-400, #aaa)',
    flexShrink: 0,
    '&:hover': {
      color: 'var(--color-danger-500, #d32f2f)'
    }
  },
  emptyHint: {
    color: 'var(--color-neutral-400, #aaa)',
    fontSize: 12,
    fontStyle: 'italic',
    padding: 8,
    textAlign: 'center'
  },
  depthIndicator: {
    fontSize: 11,
    color: 'var(--color-neutral-500, #888)',
    marginRight: 4,
    minWidth: 20,
    textAlign: 'center'
  }
}))

function DraggableGroupingItem ({ grouping, index, moveItem, onRemove }) {
  const classes = useStyles()
  const ref = useRef(null)

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPE,
    item: () => ({ index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  const [, drop] = useDrop({
    accept: DRAG_TYPE,
    hover (item, monitor) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      moveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    }
  })

  drag(drop(ref))

  return (
    <div
      ref={ref}
      className={`${classes.dragItem} ${isDragging ? classes.dragItemDragging : ''}`}
    >
      <Icon name='dragIndicator' customSize={14} additionalClasses={classes.dragHandle} />
      <span className={classes.depthIndicator}>{index + 1}.</span>
      <span className={classes.dragLabel}>{grouping}</span>
      <span className={classes.removeBtn} onClick={() => onRemove(grouping)}>
        <Icon name='close' customSize={14} />
      </span>
    </div>
  )
}

DraggableGroupingItem.propTypes = {
  grouping: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  moveItem: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
}

export default function CustomGroupingDialog ({ open, onClose, currentGroupings, onApply }) {
  const classes = useStyles()
  const [selected, setSelected] = useState(currentGroupings || [])

  useEffect(() => {
    if (open) setSelected(currentGroupings || [])
  }, [open, currentGroupings])

  const handleToggle = useCallback((value) => {
    setSelected(prev =>
      prev.includes(value) ? prev.filter(g => g !== value) : [...prev, value]
    )
  }, [])

  const handleRemove = useCallback((value) => {
    setSelected(prev => prev.filter(g => g !== value))
  }, [])

  const moveItem = useCallback((fromIndex, toIndex) => {
    setSelected(prev => {
      const next = [...prev]
      const [moved] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, moved)
      return next
    })
  }, [])

  const handleApply = useCallback(() => {
    onApply(selected)
    onClose()
  }, [selected, onApply, onClose])

  return (
    <SydModal open={open} onClose={onClose} title='Custom Grouping' size='small'>
      <DndProvider backend={HTML5Backend}>
        <div className={classes.body}>
          <div className={classes.column}>
            <div className={classes.columnHeader}>Available Groupings</div>
            <div className={classes.optionList}>
              {GROUPING_OPTIONS.map(opt => (
                <FormControlLabel
                  key={opt.value}
                  className={classes.optionLabel}
                  control={
                    <Checkbox
                      checked={selected.includes(opt.value)}
                      onChange={() => handleToggle(opt.value)}
                      size='small'
                      color='primary'
                    />
                  }
                  label={opt.label}
                />
              ))}
            </div>
          </div>
          <div className={classes.column}>
            <div className={classes.columnHeader}>Grouping Order (drag to reorder)</div>
            <div className={classes.selectedList}>
              {selected.length === 0 && (
                <div className={classes.emptyHint}>Select groupings from the left</div>
              )}
              {selected.map((grouping, index) => (
                <DraggableGroupingItem
                  key={grouping}
                  grouping={grouping}
                  index={index}
                  moveItem={moveItem}
                  onRemove={handleRemove}
                />
              ))}
            </div>
          </div>
        </div>
      </DndProvider>
      <SydModalActions>
        <SydButton variant='outline' size='sm' onClick={onClose}>Cancel</SydButton>
        <SydButton variant='primary' size='sm' onClick={handleApply} disabled={selected.length === 0}>Apply</SydButton>
      </SydModalActions>
    </SydModal>
  )
}

CustomGroupingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentGroupings: PropTypes.arrayOf(PropTypes.string),
  onApply: PropTypes.func.isRequired
}
